import type {
  AppointmentPlanningPreviewAppointment,
  AppointmentPlanningPreviewRow,
} from "../../../../../../../../../../api/kehrblatt/planning"
import type { CalendarBoardPropertyRef } from "../../../../../../../../../../api/kehrblatt/schedule-events"
import { addDays, startOfDay } from "../../../../../../../../../../_shared/_logic/date-math"
import { internalOrderTypeLabel } from "../../../../../../../../../../_shared/_logic/order-type-labels"
import type {
  AppointmentPlanningCalendarPreviewModel,
  AppointmentPlanningCalendarSource,
} from "../../../../../_shared/_logic/calendar-contract"

type BuildAppointmentPlanningCalendarPreviewModelInput = {
  organizationId: string
  rows: AppointmentPlanningPreviewRow[]
  sources: AppointmentPlanningCalendarSource[]
  employeeLabelById: Record<string, string>
  now?: Date
}

const localDayKey = (value: Date) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, "0")
  const day = String(value.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null

  const parsed = new Date(value)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const minutesBetween = (startAt: string, endAt: string): number | null => {
  const start = parseDate(startAt)
  const end = parseDate(endAt)

  if (!start || !end || end <= start) return null

  return Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000))
}

const dedupeById = <T extends { id?: string | null }>(items: T[]): T[] => {
  const result = new Map<string, T>()
  for (const item of items) {
    const id = String(item?.id ?? "").trim()

    if (id && !result.has(id)) result.set(id, item)
  }

  return Array.from(result.values())
}

const hasCoordinates = (property: CalendarBoardPropertyRef): boolean =>
  typeof property.geo_lat === "number" &&
  Number.isFinite(property.geo_lat) &&
  typeof property.geo_lng === "number" &&
  Number.isFinite(property.geo_lng)

export const mergeAppointmentPlanningCalendarProperties = ({
  boardProperties,
  previewProperties,
  authoritativePreviewPropertyIds,
}: {
  boardProperties: CalendarBoardPropertyRef[]
  previewProperties: CalendarBoardPropertyRef[]
  authoritativePreviewPropertyIds: Set<string>
}): CalendarBoardPropertyRef[] => {
  const propertyById = new Map<string, CalendarBoardPropertyRef>()
  for (const property of boardProperties) {
    const id = String(property.id ?? "").trim()

    if (id && !propertyById.has(id)) propertyById.set(id, property)
  }
  for (const property of previewProperties) {
    const id = String(property.id ?? "").trim()

    if (!id) continue

    const boardProperty = propertyById.get(id)

    if (!boardProperty) {
      propertyById.set(id, property)
      continue
    }

    if (
      !authoritativePreviewPropertyIds.has(id) &&
      (hasCoordinates(boardProperty) || !hasCoordinates(property))
    ) {
      continue
    }

    propertyById.set(id, {
      ...boardProperty,
      geo_lat: property.geo_lat ?? null,
      geo_lng: property.geo_lng ?? null,
    })
  }

  return Array.from(propertyById.values())
}

const sameInstant = (left?: string | null, right?: string | null): boolean => {
  if (!left || !right) return false

  const leftTime = new Date(left).getTime()
  const rightTime = new Date(right).getTime()

  return Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime === rightTime
}

const sameEmployee = (left?: string | null, right?: string | null): boolean =>
  Boolean(left && right && String(left) === String(right))

const keepExistingMatchesActiveEvent = (row: AppointmentPlanningPreviewRow): boolean => {
  const appointment = row.appointment
  const active = row.active_schedule_event

  return Boolean(
    row.status === "planned" &&
    row.effective_planning_strategy === "KEEP_EXISTING" &&
    appointment &&
    active &&
    sameEmployee(appointment.employee_id, active.employee_id) &&
    sameInstant(appointment.start_at, active.start_at) &&
    sameInstant(appointment.end_at, active.end_at),
  )
}

export const hiddenBoardEventIdsForKeepExistingRows = ({
  rows,
  sources,
}: {
  rows: AppointmentPlanningPreviewRow[]
  sources: AppointmentPlanningCalendarSource[]
}): string[] => {
  const sourceByRowId = new Map(sources.map((source) => [source.rowId, source]))
  const hiddenIds = new Set<string>()
  for (const row of rows) {
    if (!keepExistingMatchesActiveEvent(row)) continue

    const source = sourceByRowId.get(row.client_row_id) ?? null
    const eventId = String(source?.workOrder?.current_schedule_event_id ?? "").trim()

    if (eventId) hiddenIds.add(eventId)
  }

  return Array.from(hiddenIds)
}

const buildSyntheticWorkOrder = ({
  organizationId,
  row,
  source,
  appointment,
  nowIso,
}: {
  organizationId: string
  row: AppointmentPlanningPreviewRow
  source: AppointmentPlanningCalendarSource | null
  appointment: AppointmentPlanningPreviewAppointment
  nowIso: string
}): WorkOrder => {
  const duration =
    source?.estimatedDurationMin ?? minutesBetween(appointment.start_at, appointment.end_at)
  const label = source?.orderType || "Terminvorschlag"

  return {
    id: `preview-work-order:${row.client_row_id}`,
    organization_id: organizationId,
    can_manage: false,
    order_number: null,
    order_types: [
      {
        order_type_id: null,
        internal_label: label,
        public_label: label,
        description: null,
      },
    ],
    property_id: source?.property?.id ?? "",
    property_unit_id: source?.unitId ?? "",
    property_unit_label: source?.unitLabel ?? null,
    status: "OPEN",
    priority: null,
    due_date: source?.dueDate ?? null,
    estimated_duration_min: duration,
    allowed_employee_ids: appointment.employee_id ? [appointment.employee_id] : [],
    assigned_employee_id: appointment.employee_id,
    current_schedule_event_id: `preview-event:${row.client_row_id}`,
    has_schedule_events: true,
    has_active_schedule_events: true,
    next_action_at: null,
    archived_at: null,
    archive_reason: null,
    recipients: [],
    created_at: nowIso,
    updated_at: nowIso,
  }
}

const buildSyntheticEvent = ({
  organizationId,
  row,
  source,
  workOrder,
  appointment,
  employeeLabelById,
  nowIso,
}: {
  organizationId: string
  row: AppointmentPlanningPreviewRow
  source: AppointmentPlanningCalendarSource | null
  workOrder: WorkOrder
  appointment: AppointmentPlanningPreviewAppointment
  employeeLabelById: Record<string, string>
  nowIso: string
}): ScheduleEvent => {
  const employeeName =
    appointment.employee_name || employeeLabelById[String(appointment.employee_id)] || "Mitarbeiter"
  const title = source?.orderType || internalOrderTypeLabel(workOrder) || "Terminvorschlag"

  return {
    id: `preview-event:${row.client_row_id}`,
    organization_id: organizationId,
    employee_id: appointment.employee_id,
    employee_name: employeeName,
    event_type: "APPOINTMENT" as ScheduleEventType,
    flex_type: appointment.flex_type as ScheduleEventFlexType,
    status: "RESERVED" as ScheduleEventStatus,
    planning_flag: "OK" as ScheduleEventPlanningFlag,
    start_at: appointment.start_at,
    end_at: appointment.end_at,
    timeframe_start_at: appointment.timeframe_start_at ?? null,
    timeframe_end_at: appointment.timeframe_end_at ?? null,
    all_day: false,
    title,
    description: "Vorschau aus der Sammelplanung",
    customer_note: null,
    location_text: null,
    property_id: source?.property?.id ?? workOrder.property_id ?? null,
    work_order_id: workOrder.id,
    source: "MANUAL" as ScheduleEventSource,
    calendar_sync: null,
    contact_order_id: null,
    created_at: nowIso,
    updated_at: nowIso,
  } as unknown as ScheduleEvent
}

export const buildAppointmentPlanningCalendarPreviewModel = ({
  organizationId,
  rows,
  sources,
  employeeLabelById,
  now = new Date(),
}: BuildAppointmentPlanningCalendarPreviewModelInput): AppointmentPlanningCalendarPreviewModel => {
  const sourceByRowId = new Map(sources.map((source) => [source.rowId, source]))
  const nowIso = now.toISOString()
  const plannedRows = rows.filter((row) => row.status === "planned" && row.appointment?.start_at)
  const previewEvents: ScheduleEvent[] = []
  const previewWorkOrders: WorkOrder[] = []
  const previewProperties: Property[] = []
  const visibleDayMap = new Map<string, Date>()
  const employeeIds: string[] = []
  const labelsByEmployeeId = { ...employeeLabelById }

  for (const row of plannedRows) {
    const appointment = row.appointment

    if (!appointment?.start_at || !appointment.end_at || !appointment.employee_id) continue

    const start = parseDate(appointment.start_at)
    const end = parseDate(appointment.end_at)

    if (!start || !end || end <= start) continue

    const source = sourceByRowId.get(row.client_row_id) ?? null
    const workOrder =
      source?.workOrder ??
      buildSyntheticWorkOrder({
        organizationId,
        row,
        source,
        appointment,
        nowIso,
      })
    const event = buildSyntheticEvent({
      organizationId,
      row,
      source,
      workOrder,
      appointment,
      employeeLabelById: labelsByEmployeeId,
      nowIso,
    })

    previewEvents.push(event)
    previewWorkOrders.push(workOrder)

    if (source?.property) previewProperties.push(source.property)

    const day = startOfDay(start)
    visibleDayMap.set(localDayKey(day), day)
    const employeeId = String(appointment.employee_id)

    if (!employeeIds.includes(employeeId)) employeeIds.push(employeeId)

    if (appointment.employee_name) labelsByEmployeeId[employeeId] = appointment.employee_name
  }

  const visibleDays = Array.from(visibleDayMap.values()).sort((a, b) => a.getTime() - b.getTime())
  const rangeStart = visibleDays[0] ?? null
  const rangeEnd = visibleDays.length ? addDays(visibleDays[visibleDays.length - 1], 1) : null

  return {
    plannedRows,
    previewEvents,
    previewWorkOrders: dedupeById(previewWorkOrders),
    previewProperties: dedupeById(previewProperties),
    hiddenBoardEventIds: hiddenBoardEventIdsForKeepExistingRows({ rows: plannedRows, sources }),
    visibleDays,
    rangeStart,
    rangeEnd,
    employeeIds,
    employeeLabelById: labelsByEmployeeId,
  }
}
